import { TABLE_PORT_CLASS, TABLE_SCROLL_CLASS } from "@ext/markdown/elements/openApi/render/openApiMarkdown";

/** What `CodeBlockPaginator` deals out, and the box it deals them out of. */
const CODE_WRAPPER_CLASS = "child-wrapper";
const CODE_LINE_CLASS = "code-line";

/** The scrolling box around a Markdown table in a description — see render/openApiMarkdown.ts. */
const PORT_SELECTOR = `.${TABLE_PORT_CLASS}`;

/**
 * Takes the scroll port out from between the box and the table, and marks what is left as the article's
 * table wrapper.
 *
 * A description table has to be dealt out row by row like any other, and the paginator that does that
 * (`TablePaginator`) owns exactly one wrapper: it clones that wrapper per page and expects the table to be
 * its first child. Handing it the port instead would leave the outer box standing in the source — the
 * pagination loop reads `source.firstElementChild` until the source is empty, so it would read the same
 * node forever — and the printed fragments would lose `.openapi-table-scroll`, the ancestor that keeps
 * their cells from breaking mid-word.
 *
 * Two levels collapse into one instead. The port exists to scroll, and paper does not scroll; the outer box
 * keeps its class, and with `data-component="table"` on it the block is picked up by the article's own
 * table handler, with no special case anywhere in the print pipeline.
 *
 * Fitting the table to the page is that handler's job too, and this file no longer does any of it: one
 * mechanism scales every printed table, whether it came from an article or from a spec's description.
 * Overflow, which is what the scaling here used to measure, cannot answer the question for an article
 * table at all — `PrintColGroup` keeps one inside the page by construction, so there is never any.
 */
export const unwrapScrollPorts = (block: HTMLElement): void => {
	for (const port of block.querySelectorAll<HTMLElement>(PORT_SELECTOR)) {
		const box = port.parentElement;
		if (!box?.classList.contains(TABLE_SCROLL_CLASS)) continue;

		box.dataset.component = "table";
		while (port.firstChild) box.insertBefore(port.firstChild, port);
		port.remove();
	}
};

/**
 * Gives a Markdown code block the shape the article's own code blocks have.
 *
 * Splitting one across pages is `CodeBlockPaginator`'s job, and it deals out `.code-line` elements from a
 * `.child-wrapper` — the markup the article renderer builds while it highlights. markdown-it writes
 * `<pre><code>…</code></pre>` and nothing else, so the paginator asserted its way out of the export the
 * first time a description's code block was too tall for what was left of a page.
 *
 * The same move as the tables above: hand the print pipeline the markup it already knows, instead of
 * teaching every paginator a second dialect. Lines are plain text — markdown-it does not highlight, and
 * paper does not need it to.
 */
export const normalizeCodeBlocks = (block: HTMLElement): void => {
	// Only what the host rendered. `markdown` is the class the viewer puts on every container it fills with
	// `renderMarkdown` output; its own code samples — request and response examples, cURL — are its markup,
	// already highlighted through `renderOpenApiCode`, and flattening those into plain lines would throw the
	// highlighting away for a shape nothing asked for.
	for (const pre of block.querySelectorAll(".markdown pre")) {
		if (pre.querySelector(`.${CODE_WRAPPER_CLASS}`)) continue;

		const source = pre.querySelector("code") ?? pre;
		const wrapper = document.createElement("div");
		wrapper.className = CODE_WRAPPER_CLASS;

		// A trailing newline is how a fence ends, not a line of its own.
		(source.textContent ?? "")
			.replace(/\n+$/, "")
			.split("\n")
			.forEach((text, index) => {
				// The wrapper preserves whitespace, and the paginator puts the same separator between the lines
				// it deals out, so a page break lands where a line break was.
				if (index) wrapper.appendChild(document.createTextNode("\n"));
				const line = document.createElement("span");
				line.className = CODE_LINE_CLASS;
				line.textContent = text;
				wrapper.appendChild(line);
			});

		pre.replaceChildren(wrapper);
	}
};

/** Everything a Markdown block in a description needs before the page it lands on is measured. */
export const prepareDescriptionBlocks = (block: HTMLElement): void => {
	unwrapScrollPorts(block);
	normalizeCodeBlocks(block);
};

export default prepareDescriptionBlocks;
